import React, { useState, useMemo } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { MdOutlineToggleOn, MdOutlineToggleOff } from 'react-icons/md';
import { LuStar, LuShoppingCart, LuMapPin } from 'react-icons/lu';

import MobileBottomNav from '../../../components/layout/MobileBottomNav';
import Button from '../../../components/common/Button';
import ImagesForm from '../../../components/store/listing/ImagesForm';
import SpecializedForm from '../../../components/store/listing/specialized/SpecializedForm';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import CategoryModal from './CategoryModal';
import LocationModal from './LocationModal';

const MobileCreateListing = ({
  form,
  errors,
  submitting,
  images,
  imageUploadProgress,
  allCategories,
  mainCategories,
  subcategories,
  categoryType,
  shouldShowCondition,
  states,
  lgas,
  lcdas,
  showLcdaField,
  loadingStates,
  loadingLgas,
  loadingLcdas,
  onBack,
  onSubmit,
  onFieldChange,
  onImagesChange,
  onMainCategorySelect,
  onSubcategorySelect,
  onStateSelect,
  onLgaSelect,
  onLcdaSelect,
  onImageUploadProgress
}) => {
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [locationModalVisible, setLocationModalVisible] = useState(false);

  /* ----------------------------------
     Helpers
  -----------------------------------*/
  const getSelectedCategoryName = () => {
    if (!form.subcategory_id && !form.category_id) return 'Select category';

    if (form.subcategory_id) {
      const sub = allCategories.find(c => c.id === +form.subcategory_id);
      const main = allCategories.find(c => c.id === +form.category_id);
      return `${main?.name} › ${sub?.name}`;
    }

    return allCategories.find(c => c.id === +form.category_id)?.name;
  };

  const getSelectedLocation = () => {
    if (!form.state_id) return 'Select location';

    const state = states.find(s => +s.value === +form.state_id);
    const lga = lgas.find(l => +l.value === +form.lga_id);
    const lcda = lcdas.find(l => +l.value === +form.lcda_id);

    let label = state?.label || '';
    if (lga) label += ` › ${lga.label}`;
    if (lcda) label += ` › ${lcda.label}`;
    return label;
  };

  const formatNumber = (value) => {
    if (!value) return '';
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  
  const unformatNumber = (value) => value.replace(/,/g, '');
  

  /* ----------------------------------
     UX: Disable submit early
  -----------------------------------*/
  const isFormIncomplete = useMemo(() => {
    return (
      !form.title ||
      !form.category_id ||
      !form.state_id ||
      !form.price ||
      images.length === 0
    );
  }, [form, images]);

  /* ----------------------------------
     UX: Scroll to first error
  -----------------------------------*/
  const handleSubmit = () => {
    if (Object.keys(errors).length > 0) {
      const firstKey = Object.keys(errors)[0];
      const el = document.querySelector(`[data-field="${firstKey}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });

      if (navigator.vibrate) navigator.vibrate(100);
      return;
    }

    onSubmit();
  };

  return (
    <div className="lg:hidden min-h-screen bg-gray-50">
      <Header />

      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="px-4 py-3 flex items-center justify-between">
          <button
            onClick={onBack}
            disabled={submitting}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold">Create Listing</h1>
          <div className="w-10" />
        </div>
      </header>

      {/* Content */}
      <div className="px-4 py-4">

        {/* Error Summary */}
        {Object.keys(errors).length > 0 && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            Please fix the highlighted fields to continue
          </div>
        )}

        {/* Title */}
        <div className="bg-white p-4 rounded-xl shadow-sm mb-4">
          <label className="text-sm font-medium mb-1 block">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            data-field="title"
            value={form.title}
            onChange={e => onFieldChange('title', e.target.value)}
            placeholder="What are you selling?"
            disabled={submitting}
            className={`w-full px-4 py-3 rounded-lg border 
              ${errors.title ? 'border-red-400' : 'border-gray-300'}
            `}
          />
          {errors.title && <p className="text-sm text-red-600 mt-2">{errors.title}</p>}
        </div>

        {/* Category */}
        <div className="bg-white p-4 rounded-xl shadow-sm mb-4">
          <label className="text-sm font-medium mb-2 block">
            Category <span className="text-red-500">*</span>
          </label>
          <button
            data-field="category"
            onClick={() => setCategoryModalVisible(true)}
            disabled={submitting}
            className={`w-full p-4 border rounded-lg flex justify-between
              ${errors.category ? 'border-red-400' : 'border-gray-300'}
            `}
          >
            <span className={form.category_id ? 'text-gray-900' : 'text-gray-500'}>
              {getSelectedCategoryName()}
            </span>
            <FiArrowLeft className="rotate-180" />
          </button>
        </div>

        {/* Images */}
        <ImagesForm
          images={images}
          errors={errors}
          submitting={submitting}
          onImagesChange={onImagesChange}
          onImageUploadProgress={onImageUploadProgress}
          imageUploadProgress={imageUploadProgress}
        />

        {/* Location */}
        <div className="bg-white p-4 rounded-xl shadow-sm mb-4">
          <label className="text-sm font-medium mb-2 block">
            Location <span className="text-red-500">*</span>
          </label>
          <button
            data-field="location"
            onClick={() => setLocationModalVisible(true)}
            disabled={submitting}
            className={`w-full p-4 border rounded-lg flex justify-between
              ${errors.location ? 'border-red-400' : 'border-gray-300'}
            `}
          >
            <span className={form.state_id ? 'text-gray-900' : 'text-gray-500'}>
              {getSelectedLocation()}
            </span>
            <FiArrowLeft className="rotate-180" />
          </button>
        </div>

        {/* Specialized */}
        {categoryType && (
          <SpecializedForm
            categoryType={categoryType}
            form={form}
            errors={errors}
            submitting={submitting}
            onFieldChange={onFieldChange}
          />
        )}

        {/* Description & Price */}
        {/* Description & Price */}
        <div className="bg-white p-4 rounded-xl shadow-sm mb-4">
          <label className="text-sm font-medium mb-2 block">Description</label>
          <textarea
            rows={4}
            value={form.description}
            onChange={e => onFieldChange('description', e.target.value)}
            placeholder="Describe your item in detail..."
            className="w-full border border-gray-300 rounded-lg px-4 py-3"
          />

          {/* Price */}
          <div className="mt-4">
            <label className="text-sm font-medium mb-2 block">
              Price <span className="text-red-500">*</span>
            </label>
            <input
              data-field="price"
              type="text"
              inputMode="numeric"
              placeholder="0.00"
              value={formatNumber(form.price)}
              onChange={(e) => {
                const rawValue = unformatNumber(e.target.value);
                if (!/^\d*$/.test(rawValue)) return;
                onFieldChange('price', rawValue);
              }}
              className={`w-full px-4 py-3 border rounded-lg mb-3
                ${errors.price ? 'border-red-400' : 'border-gray-300'}
              `}
            />
            {errors.price && <p className="text-sm text-red-600 mt-1">{errors.price}</p>}
          </div>

          {/* Negotiable Toggle */}
          <button
            type="button"
            onClick={() => onFieldChange('is_negotiable', !form.is_negotiable)}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition mb-4 ${
              form.is_negotiable 
                ? 'bg-green-50 border-green-500 text-green-700' 
                : 'bg-gray-50 border-gray-300 text-gray-700'
            }`}
          >
            {form.is_negotiable ? (
              <MdOutlineToggleOn className="w-5 h-5 text-green-500" />
            ) : (
              <MdOutlineToggleOff className="w-5 h-5 text-gray-400" />
            )}
            <span className="text-sm font-medium">
              {form.is_negotiable ? 'Price is Negotiable ✓' : 'Price is Fixed'}
            </span>
          </button>

          {/* Condition Dropdown (for non-services) */}
          {categoryType !== 'services' && shouldShowCondition() && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Condition
              </label>
              <select
                value={form.condition || 'brand_new'}
                onChange={(e) => onFieldChange('condition', e.target.value)}
                disabled={submitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white"
              >
                <option value="brand_new">Brand New</option>
                <option value="like_new">Like New</option>
                <option value="fairly_used">Fairly Used</option>
                <option value="used">Used</option>
                <option value="refurbished">Refurbished</option>
              </select>
            </div>
          )}
        </div>

        {/* Submit */}
        <Button
          onClick={handleSubmit}
          disabled={submitting || isFormIncomplete}
          loading={submitting}
          className={`w-full ${isFormIncomplete ? 'opacity-60' : ''}`}
        >
          {submitting ? 'Creating...' : 'Create Listing'}
        </Button>
      </div>

      {/* Modals */}
      {categoryModalVisible && (
        <CategoryModal
          form={form}
          mainCategories={mainCategories}
          subcategories={subcategories}
          allCategories={allCategories}
          onMainCategorySelect={onMainCategorySelect}
          onSubcategorySelect={id => {
            onSubcategorySelect(id);
            setCategoryModalVisible(false);
          }}
          onClose={() => setCategoryModalVisible(false)}
          isMobile
        />
      )}

      {locationModalVisible && (
        <LocationModal
          form={form}
          states={states}
          lgas={lgas}
          lcdas={lcdas}
          showLcdaField={showLcdaField}
          loadingStates={loadingStates}
          loadingLgas={loadingLgas}
          loadingLcdas={loadingLcdas}
          onStateSelect={onStateSelect}
          onLgaSelect={onLgaSelect}
          onLcdaSelect={id => {
            onLcdaSelect(id);
            setLocationModalVisible(false);
          }}
          onClose={() => setLocationModalVisible(false)}
          isMobile
        />
      )}

      <MobileBottomNav />
      <Footer />
    </div>
  );
};

export default MobileCreateListing;
